import { useCallback, useState } from "react";

/**
 * Which chart series the reader has switched off.
 *
 * Hiding is implemented by filtering the series array handed to the chart rather than by
 * any library visibility flag, because each series carries its own explicit colour: drop
 * one and the rest keep theirs. Letting the chart colour by position instead would repaint
 * every series after the hidden one, which is the exact thing the palette is built to
 * prevent.
 */

/**
 * Toggling the last visible series is a no-op.
 *
 * An empty chart is never what the reader meant, and a chart with no series renders as an
 * axis with nothing under it -- which reads as a failed load rather than as a choice.
 */
export const toggleSeries = (
    hidden: ReadonlySet<string>,
    id: string,
    allIds: readonly string[]
): Set<string> => {
    const next = new Set(hidden);

    if (next.has(id)) {
        next.delete(id);
        return next;
    }

    const visibleCount = allIds.filter(candidate => !next.has(candidate)).length;
    if (visibleCount <= 1) {
        return next;
    }

    next.add(id);
    return next;
};

/** True when hiding this one would empty the chart, so the legend can say so rather than act. */
export const isLastVisible = (hidden: ReadonlySet<string>, id: string, allIds: readonly string[]): boolean =>
    !hidden.has(id) && allIds.filter(candidate => !hidden.has(candidate)).length <= 1;

export interface SeriesVisibility {
    hidden: ReadonlySet<string>;
    toggle: (id: string) => void;
}

export const useSeriesVisibility = (allIds: readonly string[]): SeriesVisibility => {
    const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set<string>());

    const toggle = useCallback(
        (id: string): void => setHidden(current => toggleSeries(current, id, allIds)),
        [allIds]
    );

    return { hidden, toggle };
};
